import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { BackButton } from '@components/ui/back-button/back-button';
import { RegionTreeSelectComponent } from '@root/src/app/pages/Ui-Kit/region-tree-select/region-tree-select.component';
import { RegionService } from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import { FieldFilter, Region } from '@6grain/masofa-monolith-api-client/dictionaries';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { finalize, catchError, takeUntil } from 'rxjs/operators';
import { forkJoin, of, Subject } from 'rxjs';
import {
  CropYieldForecastService,
  CropYieldForecastCalculationLog,
  SortType,
} from '@6grain/masofa-monolith-api-client/ai-inference';
import { CropYieldForecastCalculationLogService } from '@6grain/masofa-monolith-api-client/ai-inference/api/cropYieldForecastCalculationLog.service';
import { UserService } from '@6grain/masofa-monolith-api-client/identity';
import { CustomPaginatorComponent } from '@root/src/app/pages/Ui-Kit/custom-paginator/custom-paginator.component';
import { PaginatorState } from 'primeng/paginator';
import { RegionManagerService } from '@components/openstreetmap/managers/region-manager.service';

@Component({
  selector: 'app-crop-yield-analysis',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    BackButton,
    RegionTreeSelectComponent,
    TableModule,
    ButtonModule,
    TooltipModule,
    DialogModule,
    CustomPaginatorComponent,
  ],
  templateUrl: './crop-yield-analysis.component.html',
  styleUrls: ['./crop-yield-analysis.component.scss'],
})
export class CropYieldAnalysisComponent implements OnInit, OnDestroy {
  regions: Region[] = [];
  selectedRegionId: string | null = null;
  selectedRegionName: string = '';

  isLoading = false;
  analysisLogs: CropYieldForecastCalculationLog[] = [];
  currentLang: string = 'en';

  usersMap: { [key: string]: string } = {};
  regionsMap: { [key: string]: Region } = {};
  displayErrorDialog: boolean = false;
  selectedErrorDetails: string | undefined = '';

  // Pagination
  totalCount = 0;
  pageSize = 20;
  first = 0;

  private destroy$ = new Subject<void>();

  constructor(
    private regionService: RegionService,
    private cdr: ChangeDetectorRef,
    private cropYieldForecastService: CropYieldForecastService,
    private calculationLogService: CropYieldForecastCalculationLogService,
    private userService: UserService,
    private translate: TranslateService,
    private regionManagerService: RegionManagerService,
  ) {}

  ngOnInit(): void {
    this.currentLang = this.translate.currentLang || 'en';

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event: LangChangeEvent) => {
      this.currentLang = event.lang;
      this.cdr.markForCheck();
    });
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    const logsFilters: FieldFilter[] = [];
    await this.regionManagerService.includeChildInRequest(
      { regionId: this.selectedRegionId },
      logsFilters as any,
    );

    forkJoin({
      regions: this.regionService
        .dictionariesRegionGetByQueryPost({ take: 1000 } as any)
        .pipe(catchError(() => of([]))),
      logs: this.calculationLogService
        .aiInferenceCropYieldForecastCalculationLogGetByQueryPost({
          take: this.pageSize,
          offset: this.first,
          filters: logsFilters,
          sortBy: 'CreateAt',
          sort: SortType.NUMBER_1,
        })
        .pipe(catchError(() => of([]))),
      count: this.calculationLogService
        .aiInferenceCropYieldForecastCalculationLogGetTotalCountPost({ filters: logsFilters })
        .pipe(catchError(() => of(0))),
    })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(({ regions, logs, count }) => {
        this.regions = regions || [];
        this.buildRegionMap();

        this.analysisLogs = logs || [];
        this.totalCount = count || 0;

        this.loadUserNames(this.analysisLogs);
        this.loadMissingRegions(this.analysisLogs);

        this.cdr.markForCheck();
      });
  }

  buildRegionMap(): void {
    this.regions.forEach((r) => {
      if (r.id) {
        this.regionsMap[r.id] = r;
        this.regionsMap[r.id.toLowerCase()] = r;
      }
    });
  }

  getRegionName(regionId: string | undefined): string {
    if (!regionId) return '-';

    const region = this.regionsMap[regionId] || this.regionsMap[regionId.toLowerCase()];

    if (!region) {
      return regionId;
    }

    const name =
      (region as any).names?.[this.currentLang] ||
      (region as any).names?.['en'] ||
      (region as any).name ||
      (region as any).Name ||
      'Unknown Region';

    return name;
  }

  loadMissingRegions(logs: CropYieldForecastCalculationLog[]): void {
    const missingIds = logs
      .map((l) => l.regionId)
      .filter((id) => !!id && !this.regionsMap[String(id)] && !this.regionsMap[String(id).toLowerCase()]);
    const uniqueMissingIds = [...new Set(missingIds)];

    if (uniqueMissingIds.length === 0) return;
    const requests = uniqueMissingIds.map((id) =>
      this.regionService.dictionariesRegionGetByIdIdGet(String(id)).pipe(catchError(() => of(null))),
    );
    forkJoin(requests).subscribe((results) => {
      results.forEach((region) => {
        if (region && region.id) {
          this.regionsMap[region.id] = region;
          this.regionsMap[region.id.toLowerCase()] = region;
        }
      });
      this.cdr.markForCheck();
    });
  }

  loadUserNames(logs: CropYieldForecastCalculationLog[]): void {
    const userIds = [
      ...new Set(logs.map((l) => l.createUser).filter((id) => !!id && id !== '00000000-0000-0000-0000-000000000000')),
    ];
    const idsToLoad = userIds.filter((id) => !this.usersMap[String(id)]);

    if (idsToLoad.length === 0) return;
    const requests = idsToLoad.map((id) =>
      this.userService.identityUserGetByIdIdGet(String(id)).pipe(catchError(() => of(null))),
    );
    forkJoin(requests).subscribe((users) => {
      users.forEach((user) => {
        if (user && user.id) {
          const displayName = user.userName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
          this.usersMap[user.id] = displayName || 'Unknown User';
        }
      });
      this.cdr.markForCheck();
    });
  }

  getUserName(userId: string | undefined): string {
    if (!userId) return '-';
    return this.usersMap[userId] || userId;
  }

  getBadgeClass(status: string): string {
    if (!status) return 'badge';
    switch (status) {
      case 'Completed':
        return 'active';
      case 'Processing':
        return 'processing';
      case 'Failed':
      case 'CriticalFailure':
      case 'CompletedWithErrors':
        return 'failed';
      default:
        return 'badge';
    }
  }

  onRegionSelected(regionId: string | null): void {
    this.selectedRegionId = regionId;
    this.selectedRegionName = this.getRegionName(regionId || undefined);
  }

  runAnalysis(): void {
    if (!this.selectedRegionId) return;

    this.isLoading = true;
    this.cropYieldForecastService
      .aiInferenceCropYieldForecastCalculateByRegionRegionIdPost(this.selectedRegionId)
      .pipe(
        finalize(() => {
          this.loadData();
        }),
      )
      .subscribe({
        next: (response) => {},
        error: (err) => {
          console.error('Analysis failed', err);
        },
      });
  }

  showErrorDetails(details: string | undefined | null): void {
    if (!details) return;
    this.selectedErrorDetails = details;
    this.displayErrorDialog = true;
  }

  onPageChange(event: PaginatorState): void {
    this.first = event.first || 0;
    this.loadData();
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.first = 0;
    this.loadData();
  }
}
